import { API_CONFIG } from '../config/api';
import { ServiceUser, ServiceUserListResponse, ServiceUserFilters, ServiceUserStats, DeviceDetailsResponse } from '../models/ServiceUser';
import { secureTokenManager } from '../utils/secureTokenManager';

export interface TestNotificationRequest {
  fcmToken: string;
  deviceType?: 'ios' | 'android';
  title?: string;
  body?: string;
  data?: Record<string, any>;
}

export interface TestNotificationResponse {
  success: boolean;
  message: string;
  data?: {
    messageId: string;
    fcmToken: string;
    deviceType: 'ios' | 'android';
    notification: {
      title: string;
      body: string;
    };
    sentAt: string;
  };
  error?: string;
  details?: string;
}

export class ServiceUserService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    // Debug logging removed for security

    try {
      const response = await fetch(url, config);
      
      // Response logging removed for security
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Error logging removed for security
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Success logging removed for security
      return data;
    } catch (error) {
      // Error logging removed for security
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get list of service users with pagination
  async getServiceUsers(filters: ServiceUserFilters = {}): Promise<ServiceUserListResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters (always include defaults)
    params.append('page', (filters.page || 1).toString());
    params.append('limit', (filters.limit || 10).toString());
    
    // Add search parameter (only if not empty)
    if (filters.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }
    
    // Add status filter (only if not 'all')
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
      // Filter logging removed for security
    } else {
      // Filter logging removed for security
    }
    
    // Add sorting parameters (only if specified)
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }

    const queryString = params.toString();
    const endpoint = `/api/service-users/all?${queryString}`;
    
    // Endpoint logging removed for security
    return this.makeRequest<ServiceUserListResponse>(endpoint);
  }

  // Get service user statistics
  async getServiceUserStats(): Promise<ServiceUserStats> {
    const endpoint = `/api/service-users/stats?_t=${Date.now()}`;
    const response = await this.makeRequest<{
      data: ServiceUserStats;
      success: boolean;
    }>(endpoint);
    
    return response.data;
  }

  // Get single service user by ID
  async getServiceUser(id: string): Promise<ServiceUser> {
    const endpoint = `/api/service-users/${id}?_t=${Date.now()}`;
    const response = await this.makeRequest<{ data: ServiceUser; success: boolean }>(endpoint);
    return response.data;
  }

  // Get device details for a service user by contact number
  async getDeviceDetails(contactNumber: string): Promise<DeviceDetailsResponse> {
    const params = new URLSearchParams();
    params.append('contactNumber', contactNumber);
    const endpoint = `/api/service-users/device-details?${params.toString()}`;
    return this.makeRequest<DeviceDetailsResponse>(endpoint);
  }

  // Send test notification to a device
  async sendTestNotification(request: TestNotificationRequest): Promise<TestNotificationResponse> {
    const endpoint = '/api/notifications/test';
    
    // Build request body, only including defined fields
    const requestBody: {
      fcmToken: string;
      deviceType?: 'ios' | 'android';
      title?: string;
      body?: string;
      data?: Record<string, any>;
    } = {
      fcmToken: request.fcmToken,
    };
    
    if (request.deviceType) {
      requestBody.deviceType = request.deviceType;
    }
    
    if (request.title) {
      requestBody.title = request.title;
    }
    
    if (request.body) {
      requestBody.body = request.body;
    }
    
    if (request.data && Object.keys(request.data).length > 0) {
      requestBody.data = request.data;
    }
    
    return this.makeRequest<TestNotificationResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // Test API connection
  async testConnection(): Promise<boolean> {
    try {
      // Connection test logging removed for security
      await this.makeRequest<ServiceUserListResponse>('/api/service-users/all?page=1&limit=1');
      return true;
    } catch (error) {
      console.error('❌ Service User API connection failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const serviceUserService = new ServiceUserService();
